import React, { useState } from 'react'
import SafeIcon from '../../common/SafeIcon'
import * as FiIcons from 'react-icons/fi'

const {
  FiStar,
  FiUploadCloud,
  FiMessageCircle,
  FiImage,
  FiVideo,
  FiFileText,
  FiSend
} = FiIcons

const FridayShowcase = () => {
  const [submission, setSubmission] = useState({
    title: '',
    description: '',
    reflection: '',
    attachments: []
  })
  const [gallery, setGallery] = useState([
    {
      id: 1,
      title: 'Solar System Diorama',
      description: '3D model with labeled planets and orbit paths.',
      reflection: 'I learned how scale and distance make planets feel tiny or huge.',
      attachments: ['image'],
      comments: ['Great detail on the rings!', 'Add info cards for each planet.'],
      week: 'Week 9'
    },
    {
      id: 2,
      title: 'Speech: Gratitude',
      description: 'Recorded a 3-minute talk about gratitude in daily life.',
      reflection: 'Practicing tone and pace helped me feel confident.',
      attachments: ['video', 'document'],
      comments: ['Loved the examples you shared!', 'Next time, pause longer after key points.'],
      week: 'Week 8'
    }
  ])
  const [newComment, setNewComment] = useState('')

  const handleAttachment = (type) => {
    setSubmission((prev) => ({ ...prev, attachments: [...prev.attachments, type] }))
  }

  const handleSubmit = (event) => {
    event.preventDefault()
    setGallery((prev) => [
      {
        id: prev.length + 1,
        ...submission,
        comments: [],
        week: `Week ${prev.length + 9}`
      },
      ...prev
    ])
    setSubmission({ title: '', description: '', reflection: '', attachments: [] })
  }

  const addComment = (showcaseId) => {
    if (!newComment.trim()) return
    setGallery((prev) =>
      prev.map((item) =>
        item.id === showcaseId ? { ...item, comments: [...item.comments, newComment] } : item
      )
    )
    setNewComment('')
  }

  return (
    <div className="space-y-8">
      <div className="bg-gradient-to-r from-[#1B4C87] to-[#00AAFF] rounded-2xl p-8 text-white">
        <h1 className="text-3xl font-bold mb-3">Friday Showcase</h1>
        <p className="text-blue-50 max-w-3xl">
          Celebrate weekly wins with multimedia submissions, reflective notes, and space for
          parent/teacher feedback.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Submission Form */}
        <div className="bg-white rounded-xl shadow-lg p-6 lg:col-span-2">
          <h2 className="text-xl font-bold text-gray-800 flex items-center mb-4">
            <SafeIcon icon={FiStar} className="w-5 h-5 mr-2 text-[#F6A800]" />
            Weekly Submission
          </h2>
          <form className="space-y-4" onSubmit={handleSubmit}>
            <input
              className="border border-gray-200 rounded-lg px-3 py-2 w-full"
              placeholder="Title"
              value={submission.title}
              onChange={(e) => setSubmission({ ...submission, title: e.target.value })}
              required
            />
            <textarea
              className="border border-gray-200 rounded-lg px-3 py-2 w-full"
              rows="3"
              placeholder="Project description"
              value={submission.description}
              onChange={(e) => setSubmission({ ...submission, description: e.target.value })}
              required
            />
            <textarea
              className="border border-gray-200 rounded-lg px-3 py-2 w-full"
              rows="3"
              placeholder="Reflection: What did you learn or love this week?"
              value={submission.reflection}
              onChange={(e) => setSubmission({ ...submission, reflection: e.target.value })}
              required
            />
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                className="flex items-center px-3 py-2 border border-gray-200 rounded-lg hover:bg-gray-50"
                onClick={() => handleAttachment('image')}
              >
                <SafeIcon icon={FiImage} className="w-4 h-4 mr-2 text-[#00AAFF]" />
                Add Image
              </button>
              <button
                type="button"
                className="flex items-center px-3 py-2 border border-gray-200 rounded-lg hover:bg-gray-50"
                onClick={() => handleAttachment('video')}
              >
                <SafeIcon icon={FiVideo} className="w-4 h-4 mr-2 text-[#1B4C87]" />
                Add Video
              </button>
              <button
                type="button"
                className="flex items-center px-3 py-2 border border-gray-200 rounded-lg hover:bg-gray-50"
                onClick={() => handleAttachment('document')}
              >
                <SafeIcon icon={FiFileText} className="w-4 h-4 mr-2 text-[#F6A800]" />
                Add Document
              </button>
              <span className="text-sm text-gray-600 flex items-center">
                <SafeIcon icon={FiUploadCloud} className="w-4 h-4 mr-1 text-gray-500" />
                {submission.attachments.length} file(s) added
              </span>
            </div>
            <button
              type="submit"
              className="w-full bg-[#00AAFF] text-white py-2 rounded-lg hover:bg-[#0095E0] transition-colors"
            >
              Submit Showcase
            </button>
          </form>
        </div>

        {/* Guidance Card */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h3 className="text-lg font-bold text-gray-800 mb-3">Reflection Prompts</h3>
          <ul className="space-y-2 text-gray-700 text-sm">
            <li>• What surprised you while working on this project?</li>
            <li>• Where did you persevere through challenge?</li>
            <li>• How does this connect to your goals or faith?</li>
            <li>• Who can benefit from seeing this work?</li>
          </ul>
          <div className="mt-4 p-3 rounded-lg bg-[#1B4C87]/10 text-[#1B4C87] text-sm">
            Tip: Add short video walk-throughs or time-lapse snippets to capture process, not just the
            finished product.
          </div>
        </div>
      </div>

      {/* Gallery */}
      <div className="bg-white rounded-xl shadow-lg p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-800">Showcase Gallery</h2>
          <p className="text-sm text-gray-600">Past submissions with comments and feedback.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {gallery.map((item) => (
            <div key={item.id} className="border border-gray-200 rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-gray-500">{item.week}</p>
                  <h3 className="font-bold text-gray-800">{item.title}</h3>
                </div>
                <div className="flex -space-x-1">
                  {item.attachments.includes('image') && (
                    <SafeIcon icon={FiImage} className="w-4 h-4 text-[#00AAFF]" />
                  )}
                  {item.attachments.includes('video') && (
                    <SafeIcon icon={FiVideo} className="w-4 h-4 text-[#1B4C87]" />
                  )}
                  {item.attachments.includes('document') && (
                    <SafeIcon icon={FiFileText} className="w-4 h-4 text-[#F6A800]" />
                  )}
                </div>
              </div>
              <p className="text-sm text-gray-700">{item.description}</p>
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="text-xs uppercase text-gray-500 font-semibold mb-1">Reflection</p>
                <p className="text-sm text-gray-800">{item.reflection}</p>
              </div>
              <div>
                <p className="text-xs uppercase text-gray-500 font-semibold mb-1">Feedback</p>
                <ul className="space-y-1 text-sm text-gray-700">
                  {item.comments.map((comment, index) => (
                    <li key={index} className="flex items-start">
                      <SafeIcon icon={FiMessageCircle} className="w-4 h-4 mr-2 text-[#1B4C87]" />
                      <span>{comment}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-3 flex items-center gap-2">
                  <input
                    className="border border-gray-200 rounded-lg px-3 py-2 flex-1 text-sm"
                    placeholder="Add parent/teacher feedback"
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                  />
                  <button
                    type="button"
                    onClick={() => addComment(item.id)}
                    className="p-2 rounded-lg bg-[#1B4C87] text-white hover:bg-[#163d6d]"
                  >
                    <SafeIcon icon={FiSend} className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

export default FridayShowcase
